import {getTestProps, type DataTestAttrs} from '@benchling/js-test-utils';
import {Card, Group, Text, createStyles, Flex, Badge, Box, Skeleton, Stack, Tooltip} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {forwardRef, useState} from 'react';

import {isSimpleClick, navigate, getRelativeURL, setLocationHref} from 'BenchlingUtil';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import useOnMount from 'ui-library/hooks/useOnMount';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import fuzzydate from 'util/fuzzydate';
import {callOnEnter} from 'util/Keyboard';
import sanitizeLink from 'util/sanitizeLink';
import {ElementMouseEvent} from 'util/Types';

import {
  CARD_SECTION_ROW_HEIGHT_PX,
  COMPACT_CARD_SECTION_MAX_WIDTH,
  DEFAULT_BADGE_PROPS,
  EMPTY_CARD_SECTION_HEIGHT_PX,
  LOADING_SKELETON_ROW_COUNT,
} from './HomepageConstants';
import {useIsSmallerThan} from './useIsSmallerThan';

const ROW_HEIGHT = `${CARD_SECTION_ROW_HEIGHT_PX}px`;
const ROW_PADDING = '6px 16px';

/**
 * @package
 * Sanitize row hrefs for {@link CardSectionRow} links.
 *
 * Expected inputs:
 * - Path-relative in-app URLs (e.g. `/entries/ent_abc`) — passed through for Backbone SPA navigation.
 * - Same-origin absolute URLs (e.g. `https://tenant.bnch.us/chats/...`) — kept as full URLs; simple
 *   clicks use {@link setLocationHref} for a full page load (reliable from the home sidepanel).
 * - Scheme-less hostnames and other absolute http(s) URLs — {@link sanitizeLink} prepends `https://`
 *   when no `:` is present; returned when accepted.
 *
 * Returns `undefined` for unsafe or protocol-relative URLs (`//host/path`).
 */
export function resolveCardSectionHref(rawHref: string): string | undefined {
  if (rawHref.startsWith('//')) {
    return undefined;
  }

  if (rawHref.startsWith('/')) {
    return rawHref;
  }

  if (getRelativeURL(rawHref) != null) {
    return rawHref;
  }

  return sanitizeLink(rawHref) ?? undefined;
}

/** True when the resolved href should use in-app (single-page app) Backbone navigation. */
function usesSpaNavigation(resolvedHref: string): boolean {
  return resolvedHref.startsWith('/') && !resolvedHref.startsWith('//');
}

const useStyles = createStyles((theme) => {
  const rowDivider = {borderTop: `1px solid ${theme.colors.gray[2]}`};

  return {
    clickable: {
      cursor: 'pointer',
    },
    flexGrow: {
      flexGrow: 1,
    },
    flexStart: {
      alignSelf: 'flex-start',
    },
    cardSectionRow: {
      flexWrap: 'nowrap',
      padding: ROW_PADDING,
      minHeight: ROW_HEIGHT,
    },
    cardSectionRowHover: {
      '&:hover': {
        backgroundColor: theme.colors.gray[1],
      },
    },
    cardSectionList: {
      '& > * + *': rowDivider,
      '&:not(:first-of-type) > :first-of-type': rowDivider,
    },
    loadingRow: {
      height: ROW_HEIGHT,
      padding: ROW_PADDING,
      display: 'flex',
      alignItems: 'center',

      '&:not(:first-of-type)': {
        ...rowDivider,
      },
    },
    linkReset: {
      display: 'block',
      '&, &:hover, &:visited, &:active, &:focus': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
    cardSectionRowIcon: {
      flexShrink: 0,
      '&&': {
        width: '16px',
        height: '16px',
      },
      marginRight: theme.spacing.sm,
    },
    cardSectionRowName: {
      flexShrink: 1,
      minWidth: 0,
      fontWeight: 500,
      fontSize: theme.fontSizes.lg,
    },
    cardSectionRowMeta: {
      flexShrink: 0,
      fontWeight: 500,
      marginLeft: theme.spacing.sm,
    },
    cardSectionRowRight: {
      flexShrink: 0,
      marginLeft: theme.spacing.sm,
    },
    cardSectionMessageRowClickable: {
      ...rowDivider,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    cardSectionEmptyState: {
      height: `${EMPTY_CARD_SECTION_HEIGHT_PX}px`,
    },
    cardSectionEmptyStateTitle: {
      fontWeight: theme.other.fontWeights.semibold,
    },
    badgeInner: {
      fontWeight: 600,
    },
  };
});

const useCardSectionStyles = createStyles(
  (
    theme,
    {
      cardContentHeight,
      noBorder,
      shouldAnimate,
    }: {cardContentHeight?: number; noBorder?: boolean; shouldAnimate: boolean}
  ) => ({
    card: {
      transition: shouldAnimate ? 'height 0.25s ease-out' : 'none',
      height: cardContentHeight ?? 'auto',
      backgroundColor: noBorder ? 'transparent' : undefined,
      borderRadius: noBorder ? 0 : undefined,
    },
    title: {
      fontWeight: theme.other.fontWeights.bold,
    },
  })
);

/** Card layout primitive for homepage sections (title, optional header actions, bordered body). */
const CardSection = forwardRef(
  (
    {
      title,
      rightContent,
      children,
      dataTestAttrs,
      noBorder,
    }: {
      title: React.ReactNode;
      rightContent?: React.ReactNode;
      children: React.ReactNode;
      dataTestAttrs: DataTestAttrs;
      noBorder?: boolean;
    },
    ref: React.Ref<HTMLDivElement>
  ): JSX.Element => {
    const {ref: cardContentRef, height: cardContentHeight} = useResizeObserver();
    // Delay the animation until the sidepanel has fully expanded, otherwise cards animate as they're squished
    const [shouldAnimate, setShouldAnimate] = useState(false);
    useOnMount(() => {
      const timeoutId = setTimeout(() => {
        setShouldAnimate(true);
      }, 250); // sidepanel.less transition duration
      return () => {
        clearTimeout(timeoutId);
      };
    });
    const {classes} = useCardSectionStyles({cardContentHeight, noBorder, shouldAnimate});

    const titleId = useId();

    return (
      <div ref={ref} role="region" aria-labelledby={titleId} {...getTestProps(dataTestAttrs)}>
        <Group position="apart" p="12px 0">
          <Text size="xl" className={classes.title} id={titleId}>
            {title}
          </Text>
          {rightContent}
        </Group>
        <Card radius="lg" withBorder={!noBorder} p="0" className={classes.card}>
          <div ref={cardContentRef}>{children}</div>
        </Card>
      </div>
    );
  }
);
CardSection.displayName = 'CardSection';

export default CardSection;

export interface CardSectionRowProps {
  id: string;
  name: React.ReactNode;
  modifiedAt?: string;
  icon?: BenchlingIconName;
  isSmall?: boolean;
  count?: number;
  href?: string;
  onClick?: () => void;
  rightContent?: React.ReactNode;
  renderRightContent?: (isSmall: boolean) => React.ReactNode;
}

function CardSectionRowContent({
  isSmall,
  icon,
  name,
  modifiedAt,
  count,
  rightContent,
  renderRightContent,
}: Pick<
  CardSectionRowProps,
  'isSmall' | 'icon' | 'name' | 'modifiedAt' | 'count' | 'rightContent' | 'renderRightContent'
>): JSX.Element {
  const {classes} = useStyles();
  const renderedRightContent =
    renderRightContent != null ? renderRightContent(isSmall ?? false) : rightContent;

  return (
    <Flex align="center" className={classes.cardSectionRow}>
      {icon && <BenchlingIcon icon={icon} className={classes.cardSectionRowIcon} />}
      <Tooltip label={name} position="bottom" openDelay={1000} color="dark">
        <Text className={classes.cardSectionRowName} truncate={true}>
          {name}
        </Text>
      </Tooltip>
      {modifiedAt != null && (
        <Text className={classes.cardSectionRowMeta} color="gray.6" fz="sm">
          {isSmall ? '' : 'Updated '}
          {fuzzydate(modifiedAt, {showTime: false, compact: isSmall})}
        </Text>
      )}
      <div className={classes.flexGrow} />
      {renderedRightContent != null && (
        <Text className={classes.cardSectionRowRight} color="gray.6" ta="right">
          {renderedRightContent}
        </Text>
      )}
      {count != null ? (
        <Badge {...DEFAULT_BADGE_PROPS} ml="md" classNames={{inner: classes.badgeInner}}>
          {count}
        </Badge>
      ) : null}
    </Flex>
  );
}

export function CardSectionRow({
  isSmall,
  icon,
  name,
  modifiedAt,
  count,
  href,
  onClick,
  rightContent,
  renderRightContent,
}: CardSectionRowProps): JSX.Element {
  const {classes, cx} = useStyles();
  const resolvedHref = href != null ? resolveCardSectionHref(href) : undefined;

  const rowContent = (
    <CardSectionRowContent
      isSmall={isSmall}
      icon={icon}
      name={name}
      modifiedAt={modifiedAt}
      count={count}
      rightContent={rightContent}
      renderRightContent={renderRightContent}
    />
  );

  let rowBody: React.ReactNode = rowContent;
  if (resolvedHref != null) {
    rowBody = (
      <a
        href={resolvedHref}
        className={cx(classes.linkReset, classes.clickable)}
        onClick={(e: ElementMouseEvent) => {
          if (!isSimpleClick(e)) {
            return;
          }
          if (usesSpaNavigation(resolvedHref)) {
            navigate(e, resolvedHref);
            onClick?.();
            return;
          }
          e.preventDefault();
          onClick?.();
          setLocationHref(resolvedHref);
        }}
      >
        {rowContent}
      </a>
    );
  } else if (href == null && onClick != null) {
    rowBody = (
      <Box
        className={cx(classes.clickable)}
        role="link"
        tabIndex={0}
        onClick={() => {
          onClick();
        }}
        onKeyDown={callOnEnter(() => {
          onClick();
        })}
      >
        {rowContent}
      </Box>
    );
  }

  return (
    <Box role="listitem" className={classes.cardSectionRowHover}>
      {rowBody}
    </Box>
  );
}

/** Renders {@link CardSectionRow} items inside a `role="list"` container. */
export function CardSectionRowList({rows}: {rows: Array<CardSectionRowProps>}): JSX.Element {
  const {classes} = useStyles();
  const {isSmallerThan, ref} = useIsSmallerThan(COMPACT_CARD_SECTION_MAX_WIDTH);

  return (
    <Box ref={ref} role="list" className={classes.cardSectionList}>
      {rows.map((row) => (
        <CardSectionRow key={row.id} isSmall={isSmallerThan} {...row} />
      ))}
    </Box>
  );
}

export function CardSectionMessageRow({
  message,
  onClick,
  dataTestAttrs,
}: {
  message: string | React.ReactNode;
  onClick?: () => void;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <Box
      className={cx(onClick && classes.clickable, onClick && classes.cardSectionMessageRowClickable)}
      {...(onClick && getActionButtonRoleProps({onPress: onClick}))}
      {...getTestProps(dataTestAttrs ?? {})}
    >
      <Flex align="center" p={ROW_PADDING}>
        <Text color={onClick ? 'blue' : 'dimmed'} fz="lg">
          {message}
        </Text>
      </Flex>
    </Box>
  );
}

/** Centered illustration and message for empty homepage card sections. */
export interface CardSectionEmptyStateProps {
  title: string | React.ReactNode;
  subtext?: string | React.ReactNode;
  illustration: React.ComponentType<React.SVGAttributes<SVGSVGElement>>;
  dataTestAttrs?: DataTestAttrs;
}

export function CardSectionEmptyState({
  title,
  subtext,
  illustration: Illustration,
  dataTestAttrs,
}: CardSectionEmptyStateProps): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack
      className={classes.cardSectionEmptyState}
      spacing="sm"
      align="center"
      justify="center"
      {...getTestProps(dataTestAttrs ?? {})}
    >
      <Illustration aria-hidden={true} />
      <Stack spacing="xs" align="center">
        <Text className={classes.cardSectionEmptyStateTitle} fz="md" ta="center">
          {title}
        </Text>
        {subtext != null ? (
          <Text color="dimmed" fz="sm" ta="center">
            {subtext}
          </Text>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function CardSectionLoadingRows({count}: {count: number}): JSX.Element {
  const {classes} = useStyles();
  return (
    <>
      {Array.from({length: count}).map((_, index) => (
        <Box
          key={`loading-${index}`}
          className={classes.loadingRow}
          {...getTestProps({component: 'CardSection', element: 'loadingRow', key: String(index)})}
        >
          <Skeleton height="20px" width="100%" radius="md" />
        </Box>
      ))}
    </>
  );
}

/** Renders a {@link CardSectionMessageRow} for loading or error query states; otherwise `null`. */
export function renderQueryLoadingOrError(
  queryResult: {type: QueryResultType},
  errorMessage: string,
  dataTestComponent: string
): JSX.Element | null {
  if (queryResult.type === QueryResultType.PENDING) {
    return <CardSectionLoadingRows count={LOADING_SKELETON_ROW_COUNT} />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <CardSectionMessageRow
        message={errorMessage}
        dataTestAttrs={{component: dataTestComponent, element: 'messageRow', key: 'error'}}
      />
    );
  }
  return null;
}
